import { ArrowDown } from 'lucide-react'
import { useEffect, useRef } from 'react'

import { Button } from '@/components/ui'
import { cn } from '@/utils'
import {
  useChatDraft,
  useChatMessages,
  useChatSending,
  useDiagnosticsEnabled,
  useLoadingMessages,
  useOptimisticUserText,
  useScrollPinnedToBottom,
  useSidebarFullScreen,
} from '../hooks'
import { buildRenderUnits } from '../utils/renderUnits'
import ChatSuggestions from './ChatSuggestions'
import AssistantBubble from './messages/AssistantBubble'
import MessageItem from './messages/MessageItem'
import UserBubble from './messages/UserBubble'

interface ChatThreadProps {
  slug: string
}

export default function ChatThread({ slug }: ChatThreadProps) {
  const messages = useChatMessages()
  const draft = useChatDraft()
  const sending = useChatSending()
  const loading = useLoadingMessages()
  const optimisticUserText = useOptimisticUserText()
  const fullScreen = useSidebarFullScreen()
  const diagnosticsEnabled = useDiagnosticsEnabled()

  const scrollRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const {
    isPinnedToBottom,
    onScroll: handleScroll,
    scrollToBottom,
  } = useScrollPinnedToBottom(scrollRef, contentRef, loading)

  // Snap to bottom the instant the user submits, even if they were scrolled
  // up reading earlier messages.
  const previousOptimisticRef = useRef<string | null>(optimisticUserText)
  useEffect(() => {
    if (optimisticUserText !== null && previousOptimisticRef.current === null) {
      scrollToBottom()
    }
    previousOptimisticRef.current = optimisticUserText
  }, [optimisticUserText, scrollToBottom])

  if (loading) return <ThreadSkeleton />

  const lastMessage = messages[messages.length - 1]
  const optimisticAlreadyPersisted =
    optimisticUserText !== null &&
    lastMessage?.role === 'user' &&
    lastMessage.contentText?.trim() === optimisticUserText.trim()
  const visibleOptimisticUserText = optimisticAlreadyPersisted
    ? null
    : optimisticUserText

  if (messages.length === 0 && !draft && !visibleOptimisticUserText) {
    return <ChatSuggestions />
  }

  const showDraftBubble =
    Boolean(draft) && (sending || (draft?.contentBlocks.length ?? 0) > 0)
  const firstAssistantId =
    messages.find((message) => message.role === 'assistant')?.id ?? null
  const draftIsFirstAssistant = firstAssistantId === null
  const renderUnits = buildRenderUnits(
    messages,
    firstAssistantId,
    !diagnosticsEnabled,
  )

  return (
    <div className="relative h-full">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        // In fullscreen, reserve scrollbar gutter on both sides so the
        // centered content column stays aligned with the (non-scrolling)
        // headers above and composer below. Without this the scrollbar
        // steals width from only the right side, shifting content left.
        // In sidebar mode the gutter would waste horizontal space, so it's
        // only applied when fullscreen.
        className={cn(
          'h-full overflow-x-hidden overflow-y-auto',
          fullScreen && '[scrollbar-gutter:stable_both-edges]',
        )}
      >
        <div
          ref={contentRef}
          className="mx-auto flex min-w-0 max-w-5xl flex-col gap-6 px-6 pt-6 pb-14"
        >
          {renderUnits.map((unit) => (
            <MessageItem
              key={unit.key}
              slug={slug}
              message={unit.message}
              artifacts={unit.outputs?.artifacts}
              toolResults={unit.outputs?.toolResults}
              showBootHeader={unit.showBootHeader}
            />
          ))}
          {visibleOptimisticUserText && (
            <UserBubble
              text={visibleOptimisticUserText}
              sender={null}
              className="animate-bubble-in"
            />
          )}
          {showDraftBubble && draft && (
            <AssistantBubble
              slug={slug}
              blocks={draft.contentBlocks}
              toolCallStatuses={draft.toolCalls}
              artifacts={draft.artifacts}
              stepUsages={draft.stepUsages}
              stepBoundaries={draft.stepBoundaries}
              isStreaming={sending}
              showBootHeader={draftIsFirstAssistant}
            />
          )}
        </div>
      </div>

      <ScrollToBottomButton
        visible={!isPinnedToBottom}
        onClick={scrollToBottom}
      />
    </div>
  )
}

function ThreadSkeleton() {
  return (
    <div className="flex flex-col gap-6 px-6 py-8">
      <SkeletonRow side="right" widthClass="w-2/3" />
      <SkeletonRow side="left" widthClass="w-5/6" lines={3} />
      <SkeletonRow side="right" widthClass="w-1/2" />
    </div>
  )
}

function SkeletonRow({
  side,
  widthClass,
  lines = 1,
}: {
  side: 'left' | 'right'
  widthClass: string
  lines?: number
}) {
  return (
    <div className={cn('flex', side === 'right' && 'justify-end')}>
      <div className={cn('flex flex-col gap-2', widthClass)}>
        {Array.from({ length: lines }).map((_unused, index) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton lines are static
            key={`skeleton-line-${index}`}
            className={cn(
              'h-3 rounded-md bg-foreground/[0.08] animate-pulse',
              index === lines - 1 ? 'w-3/4' : 'w-full',
            )}
          />
        ))}
      </div>
    </div>
  )
}

function ScrollToBottomButton({
  visible,
  onClick,
}: {
  visible: boolean
  onClick: () => void
}) {
  if (!visible) return null
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-4 flex justify-center">
      <Button
        size="sm"
        theme="secondary"
        isIcon
        onClick={onClick}
        aria-label="Scroll to latest message"
        className="pointer-events-auto h-8 w-8 rounded-full shadow-md"
      >
        <ArrowDown className="h-4 w-4" />
      </Button>
    </div>
  )
}
